import React, { useState, useEffect } from 'react';
import { Plus, Check, X, Edit2, Trash2, Target, Flag, Users } from 'lucide-react';
import { sparkleFrom } from '../lib/celebrate';

const PersonalGoals = () => {
  const [goals, setGoals] = useState([]);
  const [objectives, setObjectives] = useState([]);
  const [userRole, setUserRole] = useState(null);
  const [isMentor, setIsMentor] = useState(false);
  
  // Mentor-specific state
  const [mentees, setMentees] = useState([]);
  const [selectedMentee, setSelectedMentee] = useState(null);
  const [menteeGoals, setMenteeGoals] = useState([]);
  const [menteeObjectives, setMenteeObjectives] = useState([]);
  
  // UI state
  const [showAddGoal, setShowAddGoal] = useState(false);
  const [showAddObjective, setShowAddObjective] = useState(false);
  const [newGoalTitle, setNewGoalTitle] = useState('');
  const [newObjectiveTitle, setNewObjectiveTitle] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchGoals();
  }, []);

  // Only mentors/admins may list mentees; calling this as a mentee returns 403
  // and spams the console, so gate it on the role resolved by fetchGoals.
  useEffect(() => {
    if (isMentor) fetchMentees();
  }, [isMentor]);

  const fetchGoals = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/goals`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setGoals(data.goals || []);
        setObjectives(data.objectives || []);
        setUserRole(data.user_role);
        setIsMentor(data.is_mentor || false);
      }
    } catch (error) {
      console.error('Error fetching goals:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMentees = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/goals/mentees/list`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setMentees(data.mentees || []);
      }
    } catch (error) {
      // If user is not a mentor, this will fail silently
      console.log('User is not a mentor');
    }
  };

  const fetchMenteeGoals = async (menteeId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/goals/mentee/${menteeId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setSelectedMentee(data.mentee);
        setMenteeGoals(data.goals || []);
        setMenteeObjectives(data.objectives || []);
      }
    } catch (error) {
      console.error('Error fetching mentee goals:', error);
    }
  };

  const addGoal = async () => {
    if (!newGoalTitle.trim()) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/goals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          title: newGoalTitle, 
          type: 'goal'
        })
      });

      if (response.ok) {
        const newItem = await response.json();
        setGoals([...goals, newItem]);
        setNewGoalTitle('');
        setShowAddGoal(false);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add goal');
      }
    } catch (error) {
      console.error('Error adding goal:', error);
      alert('Failed to add goal');
    }
  };

  const addObjective = async () => {
    if (!newObjectiveTitle.trim() || !selectedMentee) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/goals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ 
          title: newObjectiveTitle, 
          type: 'objective',
          mentee_id: selectedMentee.id
        })
      });

      if (response.ok) {
        const newItem = await response.json();
        setMenteeObjectives([...menteeObjectives, newItem]);
        setNewObjectiveTitle('');
        setShowAddObjective(false);
        // Refresh mentees list to update counts
        fetchMentees();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to add objective');
      }
    } catch (error) {
      console.error('Error adding objective:', error);
      alert('Failed to add objective');
    }
  };

  const toggleComplete = async (id, completed, type, e) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/goals/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ completed: !completed })
      });

      if (response.ok) {
        const updatedItem = await response.json();
        // Celebrate when newly completing (not un-completing)
        if (!completed && e?.currentTarget) {
          sparkleFrom(e.currentTarget);
        }
        if (type === 'goal') {
          setGoals(goals.map(g => g.id === id ? updatedItem : g));
          if (selectedMentee) {
            setMenteeGoals(menteeGoals.map(g => g.id === id ? updatedItem : g));
          }
        } else {
          setObjectives(objectives.map(o => o.id === id ? updatedItem : o));
          if (selectedMentee) {
            setMenteeObjectives(menteeObjectives.map(o => o.id === id ? updatedItem : o));
          }
        }
      }
    } catch (error) {
      console.error('Error toggling complete:', error);
    }
  };

  const updateTitle = async (id, type) => {
    if (!editingTitle.trim()) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/goals/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ title: editingTitle })
      });

      if (response.ok) {
        const updatedItem = await response.json();
        if (type === 'goal') {
          setGoals(goals.map(g => g.id === id ? updatedItem : g));
          if (selectedMentee) {
            setMenteeGoals(menteeGoals.map(g => g.id === id ? updatedItem : g));
          }
        } else {
          setObjectives(objectives.map(o => o.id === id ? updatedItem : o));
          if (selectedMentee) {
            setMenteeObjectives(menteeObjectives.map(o => o.id === id ? updatedItem : o));
          }
        }
        setEditingId(null);
        setEditingTitle('');
      }
    } catch (error) {
      console.error('Error updating:', error);
    }
  };

  const deleteItem = async (id, type) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const token = localStorage.getItem('access_token');
      
      const response = await fetch(`${backendUrl}/api/goals/${id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        if (type === 'goal') {
          setGoals(goals.filter(g => g.id !== id));
          if (selectedMentee) {
            setMenteeGoals(menteeGoals.filter(g => g.id !== id));
          }
        } else {
          setObjectives(objectives.filter(o => o.id !== id));
          if (selectedMentee) {
            setMenteeObjectives(menteeObjectives.filter(o => o.id !== id));
          }
        }
        // Refresh mentees list to update counts
        if (isMentor) {
          fetchMentees();
        }
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete');
      }
    } catch (error) {
      console.error('Error deleting:', error);
    }
  };

  const renderItem = (item, type, canEdit = true, canDelete = true) => (
    <div key={item.id} className="flex items-center space-x-3 p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors group">
      <button
        onClick={(e) => toggleComplete(item.id, item.completed, type, e)}
        className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${
          item.completed
            ? 'bg-green-500 border-green-500'
            : type === 'goal' 
              ? 'border-purple-400 hover:border-purple-600'
              : 'border-orange-400 hover:border-orange-600'
        }`}
      >
        {item.completed && <Check className="w-3 h-3 text-white" />}
      </button>
      
      {editingId === item.id ? (
        <div className="flex-1 flex items-center space-x-2">
          <input
            type="text"
            value={editingTitle}
            onChange={(e) => setEditingTitle(e.target.value)}
            className="flex-1 px-2 py-1 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-purple-500"
            autoFocus
            onKeyPress={(e) => e.key === 'Enter' && updateTitle(item.id, type)}
          />
          <button
            onClick={() => updateTitle(item.id, type)}
            className="p-1 text-green-600 hover:text-green-700"
          >
            <Check className="w-4 h-4" />
          </button>
          <button
            onClick={() => {
              setEditingId(null);
              setEditingTitle('');
            }}
            className="p-1 text-red-600 hover:text-red-700"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <>
          <div className="flex-1">
            <span className={`text-sm block ${item.completed ? 'line-through text-gray-500' : 'text-gray-700'}`}>
              {item.title}
            </span>
            {item.created_by_name && type === 'objective' && (
              <span className="text-xs text-gray-500 mt-1">Set by: {item.created_by_name}</span>
            )}
          </div>
          {(canEdit || canDelete) && (
            <div className="flex items-center space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
              {canEdit && (
                <button
                  onClick={() => {
                    setEditingId(item.id);
                    setEditingTitle(item.title);
                  }}
                  className="p-1 text-gray-600 hover:text-purple-600"
                >
                  <Edit2 className="w-3 h-3" />
                </button>
              )}
              {canDelete && (
                <button
                  onClick={() => deleteItem(item.id, type)}
                  className="p-1 text-gray-600 hover:text-red-600"
                >
                  <Trash2 className="w-3 h-3" />
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      </div>
    );
  }

  // MENTOR VIEW
  if (isMentor) {
    return (
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-purple-100 rounded-lg">
              <Users className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Mentor Dashboard</h2>
              <p className="text-sm text-gray-600">Manage your mentees' goals and objectives</p>
            </div>
          </div>
        </div>

        {/* Mentees List */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-3">Your Mentees</h3>
          {mentees.length === 0 ? (
            <p className="text-gray-500 text-sm">No mentees assigned yet</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {mentees.map(mentee => (
                <button
                  key={mentee.id}
                  onClick={() => fetchMenteeGoals(mentee.id)}
                  className={`p-4 rounded-lg border-2 text-left transition-all ${
                    selectedMentee?.id === mentee.id
                      ? 'border-purple-500 bg-purple-50'
                      : 'border-gray-200 hover:border-purple-300'
                  }`}
                >
                  <p className="font-semibold text-gray-900">{mentee.name || mentee.email}</p>
                  <p className="text-xs text-gray-500 mt-1">{mentee.enrolled_mentorship_track || 'No track'}</p>
                  <div className="flex items-center space-x-4 mt-2 text-xs">
                    <span className="text-purple-600">{mentee.goals_count || 0} Goals</span>
                    <span className="text-orange-600">{mentee.objectives_count || 0} Objectives</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Selected Mentee Details */}
        {selectedMentee && (
          <div className="border-t pt-6">
            <div className="mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Managing: {selectedMentee.name || selectedMentee.email}</h3>
            </div>

            {/* Mentee's Goals (Read-Only for Mentor) */}
            <div className="mb-6">
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-5 h-5 text-purple-600" />
                <h4 className="font-semibold text-gray-900">Mentee's Personal Goals</h4>
                <span className="text-sm text-gray-500">({menteeGoals.length}/10)</span>
              </div>
              {menteeGoals.length === 0 ? (
                <p className="text-gray-500 text-sm italic">Mentee hasn't set any goals yet</p>
              ) : (
                <div className="space-y-2">
                  {menteeGoals.map(goal => renderItem(goal, 'goal', false, false))}
                </div>
              )}
            </div>

            {/* Set Objectives for Mentee */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Flag className="w-5 h-5 text-orange-600" />
                  <h4 className="font-semibold text-gray-900">Objectives You Set</h4>
                  <span className="text-sm text-gray-500">({menteeObjectives.length}/10)</span>
                </div>
                {menteeObjectives.length < 10 && (
                  <button
                    onClick={() => setShowAddObjective(!showAddObjective)}
                    className="flex items-center space-x-1 px-3 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors text-sm"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Objective</span>
                  </button>
                )}
              </div>

              {showAddObjective && (
                <div className="mb-3 p-3 bg-orange-50 rounded-lg">
                  <input
                    type="text"
                    value={newObjectiveTitle}
                    onChange={(e) => setNewObjectiveTitle(e.target.value)}
                    placeholder="Enter objective for this mentee..."
                    className="w-full px-3 py-2 border border-orange-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 text-sm"
                    onKeyPress={(e) => e.key === 'Enter' && addObjective()}
                    autoFocus
                  />
                  <div className="flex items-center space-x-2 mt-2">
                    <button
                      onClick={addObjective}
                      className="px-3 py-1 bg-orange-600 text-white rounded-lg hover:bg-orange-700 text-sm"
                    >
                      Add
                    </button>
                    <button
                      onClick={() => {
                        setShowAddObjective(false);
                        setNewObjectiveTitle('');
                      }}
                      className="px-3 py-1 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              {menteeObjectives.length === 0 ? (
                <p className="text-gray-500 text-sm italic">No objectives set yet</p>
              ) : (
                <div className="space-y-2">
                  {menteeObjectives.map(obj => renderItem(obj, 'objective', true, true))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  // MENTEE VIEW
  return (
    <div className="bg-white rounded-lg shadow-sm p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="p-2 bg-gradient-to-r from-purple-500 to-orange-500 rounded-lg">
          <Target className="w-6 h-6 text-white" />
        </div>
        <div>
          <h2 className="text-xl font-bold text-gray-900">Personal Goals & Objectives</h2>
          <p className="text-sm text-gray-600">Track your spiritual growth journey</p>
        </div>
      </div>

      {/* Personal Goals Section */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-2">
            <Target className="w-5 h-5 text-purple-600" />
            <h3 className="text-lg font-semibold text-gray-900">My Personal Goals</h3>
            <span className="text-sm text-gray-500">({goals.length}/10)</span>
          </div>
          {goals.length < 10 && (
            <button
              onClick={() => setShowAddGoal(!showAddGoal)}
              className="flex items-center space-x-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors"
            >
              <Plus className="w-4 h-4" />
              <span>Add Goal</span>
            </button>
          )}
        </div>

        {showAddGoal && (
          <div className="mb-4 p-4 bg-purple-50 rounded-lg">
            <input
              type="text"
              value={newGoalTitle}
              onChange={(e) => setNewGoalTitle(e.target.value)}
              placeholder="What do you want to achieve?"
              className="w-full px-4 py-2 border border-purple-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              onKeyPress={(e) => e.key === 'Enter' && addGoal()}
              autoFocus
            />
            <div className="flex items-center space-x-2 mt-3">
              <button
                onClick={addGoal}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
              >
                Add Goal
              </button>
              <button
                onClick={() => {
                  setShowAddGoal(false);
                  setNewGoalTitle('');
                }}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {goals.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Target className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>No goals yet. Start by adding your first goal!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {goals.map(goal => renderItem(goal, 'goal'))}
          </div>
        )}
      </div>

      {/* Mentor-Set Objectives Section */}
      <div className="border-t pt-6">
        <div className="flex items-center space-x-2 mb-4">
          <Flag className="w-5 h-5 text-orange-600" />
          <h3 className="text-lg font-semibold text-gray-900">Objectives from Your Mentor</h3>
          <span className="text-sm text-gray-500">({objectives.length})</span>
        </div>

        {objectives.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Flag className="w-12 h-12 mx-auto mb-3 opacity-50" />
            <p>Your mentor hasn't set any objectives yet</p>
          </div>
        ) : (
          <div className="space-y-2">
            {objectives.map(obj => renderItem(obj, 'objective', false, false))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PersonalGoals;
